import { useAppDispatch } from '@/shared/hooks';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { useSuccessMessage } from '@/shared/hooks/useSuccessMessage';
import type { EntityApi, IdentifiableEntity } from '@/shared/types/entity';
import { unwrapDispatchResult } from '@/shared/utils/redux';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export interface UseEntityFormConfig<T extends IdentifiableEntity, D> {
  api: EntityApi<T, D>;
  createAction: (data: D) => unknown;
  updateAction: (payload: { id: number; data: Partial<D> }) => unknown;
  listEndpoint: string;
  entityName: string;
}

export const useEntityForm = <T extends IdentifiableEntity, D>({
  api,
  createAction,
  updateAction,
  listEndpoint,
  entityName,
}: UseEntityFormConfig<T, D>) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [entity, setEntity] = useState<T | null>(null);
  const { showSuccess } = useSuccessMessage();
  const dispatchAndUnwrap = useCallback(
    <R>(action: unknown): Promise<R> => {
      return unwrapDispatchResult<R>(dispatch(action as never));
    },
    [dispatch],
  );

  const { error, handleError, clearError } = useErrorHandler({
    defaultMessage: `Failed to process ${entityName}`,
    onError: (err) => {
      console.error(`${entityName} form error:`, err);
    },
  });

  // Load entity data
  useEffect(() => {
    if (!id || id === 'new') return;

    const loadEntity = async () => {
      try {
        const response = await api.getOne(Number(id));
        setEntity(response);
        clearError();
      } catch (err) {
        handleError(err);
      }
    };

    void loadEntity();
  }, [id, api, clearError, handleError]);

  const handleSubmit = useCallback(
    async (data: D) => {
      clearError();
      try {
        if (id && id !== 'new') {
          await dispatchAndUnwrap<T>(updateAction({ id: Number(id), data }));
          showSuccess(`${entityName} updated successfully!`);
          // Reload entity data to reflect changes
          const updatedEntity = await api.getOne(Number(id));
          setEntity(updatedEntity);
          clearError();
        } else {
          const result = await dispatchAndUnwrap<T>(createAction(data));
          showSuccess(`${entityName} created successfully!`);
          const newId = result.id;
          void navigate(`${listEndpoint}/${newId}`.replace('/admin', ''));
        }
      } catch (err) {
        handleError(err);
      }
    },
    [
      dispatchAndUnwrap,
      id,
      navigate,
      createAction,
      updateAction,
      listEndpoint,
      handleError,
      entityName,
      api,
      showSuccess,
      clearError,
    ],
  );

  return {
    entity,
    error,
    handleSubmit,
    isEdit: Boolean(id && id !== 'new'),
  };
};
